import { useEffect, useRef, useState, type ReactElement } from "react";

/**
 * Three.js is an npm dependency and is bundled into ``dist/assets/vendor-three-*.js``.
 * No CDN and no separate download on the VPS — rebuild the frontend and deploy ``dist``.
 */
export function LoginScene3D({
  onReadyChange,
}: {
  onReadyChange?: (ready: boolean) => void;
} = {}): ReactElement {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Preparing 3D scene…");
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let disposed = false;
    let disposeScene: (() => void) | null = null;
    let timeoutId = 0;
    let fadeTimer = 0;
    let stepTimer = 0;

    onReadyChange?.(false);

    const clearWait = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = 0;
      }
      if (stepTimer) {
        window.clearInterval(stepTimer);
        stepTimer = 0;
      }
    };

    const finishLoading = () => {
      if (disposed) return;
      clearWait();
      setFadeOut(true);
      fadeTimer = window.setTimeout(() => {
        if (disposed) return;
        setLoading(false);
        onReadyChange?.(true);
      }, 420);
    };

    const markError = (msg: string) => {
      if (disposed) return;
      clearWait();
      setError(msg || "3D scene failed to initialize.");
      setLoading(false);
      onReadyChange?.(true);
    };

    timeoutId = window.setTimeout(() => {
      if (disposed) return;
      setStatus("Opening login…");
      finishLoading();
    }, 45000);

    const steps = [
      "Loading local Three.js bundle…",
      "Preparing WebGL…",
      "Building infrastructure scene…",
      "Almost ready…",
    ];
    let step = 0;
    setStatus(steps[0]);
    stepTimer = window.setInterval(() => {
      if (disposed) return;
      step = Math.min(step + 1, steps.length - 1);
      setStatus(steps[step]);
    }, 1600);

    void import("./mountLoginScene")
      .then(({ mountLoginScene }) => {
        if (disposed || !rootRef.current) return;
        setStatus("Building infrastructure scene…");
        disposeScene = mountLoginScene(rootRef.current, {
          onReady: finishLoading,
          onError: markError,
        });
      })
      .catch((err: unknown) => {
        markError(err instanceof Error ? err.message : "THREE.JS LOCAL BUNDLE FAILED TO LOAD");
      });

    return () => {
      disposed = true;
      clearWait();
      if (fadeTimer) window.clearTimeout(fadeTimer);
      disposeScene?.();
    };
  }, [onReadyChange]);

  return (
    <>
      <div ref={rootRef} className="login-scene-root" id="three-root" />
      {loading ? (
        <div className={`login-scene-loading${fadeOut ? " done" : ""}`} role="status" aria-live="polite">
          <div className="login-boot-orb" aria-hidden>
            <span className="login-boot-ring" />
            <span className="login-boot-core" />
          </div>
          <p className="login-boot-status">{status}</p>
          <div className="login-boot-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="login-scene-banner" role="status">
          {error}
        </div>
      ) : null}
    </>
  );
}
